import { ChangeDetectorRef, Component, ElementRef, HostListener, Inject, NgZone, OnDestroy, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/core/services/auth.service';
import { HealthEditDialogComponent } from '../edit-health-dialog/edit-health-dialog.component';
import { AllergyDialogComponent } from '../allergy/allergy.component';
import { MedicationDialogComponent } from '../medication/medication.component';
import { AddDevicesComponent, AddVitalsComponent } from '../devices/devices.component';
import { format, parse } from 'date-fns';
import { DailyHealthTip, getDailyHealthTip } from './health-tips.data';
import { of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { AllergyView, DashboardView, mapDashboardResponse, DashboardResponse,
         MedicationView, VitalView, FamilyHistoryView, InsuranceView, ImmunizationView,
         DashboardDocumentsResponse, HealthDocumentsView, mapHealthDocuments } from './health-dashboard.model';

/** One panel inside an info-card carousel. */
interface DashSlide {
  kind: 'summary' | 'add' | 'empty';
  title: string;
  subtitle: string;
  footLabel: string;
  footValue: string;
  actionLabel?: string;   // 'empty' panels only: text of the "+ Add ..." action
  docId?: string;         // Health Documents only: id passed to the Documents module preview
  footTone?: 'danger';    // Health Documents only: expiry value shown in red
  note?: string;          // Vitals only: the API message of the reading (tooltip)
  icon?: string;          // Vitals only: tile icon of this reading (overrides the card icon)
}

/** One record of a card (becomes one carousel panel). */
interface DashRecord {
  title: string;
  subtitle: string;
  footValue: string;
  footLabel?: string;
  footTone?: 'danger';
  docId?: string;
  note?: string;
  icon?: string;
}

/** Panels shown per card; "View all" opens the full list. */
const MAX_DASH_RECORDS = 5;

/** One info card (Medication, Insurance, ...) rendered by the template with *ngFor. */
interface DashCard {
  id: string;
  title: string;
  image: string;
  tint: 'blue' | 'green' | 'orange' | 'purple';
  route: string | null;       // null = UI-only card (no screen/API yet); same routes the old cards passed to checkHealthUpdates()
  addCategory: string | null; // same categories the old "+" icons passed to handlePlusClick()
  footIcon: string;
  icon?: string;              // tile icon key (inline SVG in the template); falls back to `image` when absent
  status?: 'loading' | 'error'; // drives the skeleton / error look of a single card
  addTip?: string;              // tooltip / aria-label of the compact "+" in the card header
  canAddRecord?: boolean;       // false = no add flow yet (Vitals, Health Documents): the empty action is shown disabled
  slides: DashSlide[];
}

/** Tooltip / accessible label of each card's compact "+" action. */
const ADD_TIPS: { [id: string]: string } = {
  medication: 'Add medication',
  vitals: 'Add vital',
  documents: 'Add document',
  insurance: 'Add insurance',
  immunization: 'Add immunization',
  allergy: 'Add allergy',
  familyhistory: 'Add family history',
  devices: 'Connect health device'
};

/**
 * Which dashboard sections are visible. Flip a flag to true to bring a section back;
 * no code or service for the hidden sections has been removed.
 */
const DASHBOARD_SECTIONS: { [id: string]: boolean } = {
  medication: true,
  vitals: true,
  documents: true,
  insurance: true,
  immunization: true,
  allergy: true,
  familyhistory: true,
  devices: true,
  careplan: false,          // future
  medicalcondition: false   // future
};

/**
 * Connected Health is meant for third-party health apps / wearables (Samsung Health, Google Fit, Apple Health...).
 * None is integrated yet, and nothing in this component tells such a connection apart from the device records of
 * the existing backend workflow. While false the card shows its empty state. Flip to true (or replace with a check
 * on a real backend field) once an integration exists. Do not hide devices by name or by OFFLINE status.
 */
const THIRD_PARTY_INTEGRATIONS_LIVE = false;

@Component({
    selector: 'app-healthdashbord',
    templateUrl: './healthdashbord.component.html',
    styleUrls: ['./healthdashbord.component.css'],
    standalone: false
})
export class HealthdashbordComponent implements OnDestroy {
  profileData: any;
  profileImg: string = '';
  country:any;
  height:any;
  weight:any;
  name:any;
  ethnicityList:any;
  BloodGroupList:any;
  medication:any;
  medcount = 0;
  devicecount:any;
  careplan:any;
  insurance:any;
  allergies:any;
  famhistory:any;
  medcondition:any;
  immunization:any;
  showMedication = false;
  careplanid:any;
  showCarePlan = false;
  isLoadingProfile: boolean = true;
  pageLoader = true;             // full-screen logo spinner (same one as the Vitals screen) while the dashboard first loads
  private destroy$ = new Subject<void>();   // cancels in-flight calls when the user leaves (browser back, menu...)
  private profileDialogRef: MatDialogRef<HealthEditDialogComponent> | null = null;
  // "Complete your health profile first" tip shown next to the add action the user clicked
  profileTip: { left: number; width: number; top: number | null; bottom: number | null; arrow: number } | null = null;
  private profileTipTimer: any;
  private profileTipOpenedAt = 0;
  dashboardError = false;        // GET consumer/dashboard/ failed: the cards fed by it show the error state, nothing else breaks
  allergyList: AllergyView[] = []; // allergies from the dashboard API (allergicReaction is always an array)
  medicationList: MedicationView[] | null = null;   // data.medications (null until the dashboard API answered with that key)
  vitalList: VitalView[] = [];                      // data.vitals: one entry per reading
  familyList: FamilyHistoryView[] = [];             // data.familyHistory
  insuranceList: InsuranceView[] = [];              // data.insurance
  immunizationList: ImmunizationView[] = [];        // data.immunization
  private dashboardProfileApplied = false; // true once the profile card shows values from the dashboard API
  deviceList: any[] = [];        // Connected Health: response of the existing device_list() API
  devicesLoaded = false;
  deviceError = false;
  healthDocs: HealthDocumentsView | null = null;   // citizen_primary documents from GET consumer/v3/dashboard/documents?tag=health
  documentsLoading = true;
  documentsError = false;        // only the Health Documents card shows the error state
  medicationCount: any
  lastUpdated:string = '';
  totalGoals:any[]=[];

  // Greeting row + daily health tip
  greeting = '';
  dailyTip!: DailyHealthTip;
  private refreshTimer: any;

  // Info-card carousels + Add Record menu
  slideIndex: { [cardId: string]: number } = {};
  addMenuOpen = false;
  // Cards of the Add Record modal, in display order (two columns). `icon` = the same icon names the info cards use.
  readonly addMenuItems = [
    { label: 'Medication',       category: 'medication',    icon: 'pill',    desc: 'Add a new medication',            color: '#0066cc', bg: '#eff6ff' },
    { label: 'Vital Reading',    category: 'vitals',        icon: 'vitalsecg', desc: 'Log BP, glucose, weight...',      color: '#0066cc', bg: '#eff6ff' },
    { label: 'Health Document',  category: 'documents',     icon: 'doc',     desc: 'Upload a report or scan',         color: '#0066cc', bg: '#eff6ff' },
    { label: 'Insurance',        category: 'insurance',     icon: 'insurance', desc: 'Add an insurance policy',         color: '#0066cc', bg: '#eff6ff' },
    { label: 'Immunization',     category: 'immunization',  icon: 'syringe', desc: 'Log a vaccine or booster',        color: '#0066cc', bg: '#eff6ff' },
    { label: 'Allergy',          category: 'allergy',       icon: 'allergy', desc: 'Record a new allergy',            color: '#0066cc', bg: '#eff6ff' },
    { label: 'Family History',   category: 'familyhistory', icon: 'users',   desc: 'Add a family health condition',   color: '#0066cc', bg: '#eff6ff' },
    { label: 'Connected Device', category: 'device',        icon: 'phone',   desc: 'Link a health app',   color: '#0066cc', bg: '#eff6ff' }
  ].filter(i => DASHBOARD_SECTIONS[i.category === 'device' ? 'devices' : i.category]);
  @ViewChild('addMenuTpl') private addMenuTpl!: TemplateRef<unknown>;
  @ViewChild('comingSoonTpl') private comingSoonTpl!: TemplateRef<unknown>;   // Samsung Health "Coming Soon" dialog
  private comingSoonRef: MatDialogRef<unknown> | null = null;
  private addRecordRef: MatDialogRef<unknown> | null = null;
  @ViewChildren('track') private trackRefs!: QueryList<ElementRef<HTMLElement>>;
  private cardsKey = '';
  private cardsCache: DashCard[] = [];
  private drag: { el: HTMLElement; startX: number; startScroll: number; startIndex: number; moved: boolean } | null = null;
  private suppressClick = false;

  constructor(private dialog: MatDialog,  private toastr: ToastrService,
      private router: Router,
      private sanitizer: DomSanitizer,
      private auth: AuthService, 
      private cdr: ChangeDetectorRef,
      private zone: NgZone,
      private host: ElementRef<HTMLElement>
    ) {}

    
    ngOnInit(){
      this.refreshTimeBasedContent();
      this.getProfile(true);   // initial load: spinner + one-time "complete your profile" dialog for a new account
      this.getActivityLog();   // "Last updated" of the Health Log strip (the profile API used to provide it)
      this.getDevices();
      this.getHealthDocuments();
      this.getCarePlan();
      // this.getCountLogs();
      // this.getMedication();
      // this.getActivityLog();
      this.profileImg = localStorage.getItem('profileImg') || '';
      this.country = localStorage.getItem('primaryCountry') || '';

    }
  
    // ---------- Greeting + daily tip (time based) ----------

    /** First name for the greeting: existing profile data, then the localStorage fallback already used in getProfile(). */
    get firstName(): string {
      const raw = this.profileData?.firstName || localStorage.getItem('first_name') || '';
      return String(raw).trim().split(/\s+/)[0] || '';
    }

    /** DOB for the Profile card. Adjust the field names here if your profile API differs. */
    get dobDisplay(): string {
      const raw = this.profileData?.dob ?? this.profileData?.dateOfBirth;
      if (!raw) return '';
      const d = new Date(raw);
      return isNaN(d.getTime()) ? String(raw) : format(d, 'MMM d, yyyy');
    }

    /** 05:00-11:59 morning, 12:00-16:59 afternoon, 17:00-20:59 evening, 21:00-04:59 night (local time). */
    getGreeting(hour: number): string {
      if (hour >= 5 && hour < 12) return 'Good morning';
      if (hour >= 12 && hour < 17) return 'Good afternoon';
      if (hour >= 17 && hour < 21) return 'Good evening';
      return 'Good night';
    }

    private refreshTimeBasedContent(): void {
      const now = new Date();
      this.greeting = this.getGreeting(now.getHours());
      this.dailyTip = getDailyHealthTip(now);
      this.scheduleNextRefresh();
    }

    /** One single timeout to the next boundary (05:00, 12:00, 17:00, 21:00 or midnight) - no polling interval. */
    private scheduleNextRefresh(): void {
      clearTimeout(this.refreshTimer);
      const now = new Date();
      const next = new Date(now);
      const nextHour = [5, 12, 17, 21].find(h => now.getHours() < h);
      if (nextHour !== undefined) {
        next.setHours(nextHour, 0, 0, 0);
      } else {
        next.setHours(24, 0, 0, 0); // next midnight -> new daily tip
      }
      this.refreshTimer = setTimeout(
        () => this.refreshTimeBasedContent(),
        next.getTime() - now.getTime() + 500
      );
    }

    /** Catches up after the laptop slept or the tab was in the background. */
    @HostListener('document:visibilitychange')
    onVisibilityChange(): void {
      if (document.visibilityState === 'visible') {
        this.refreshTimeBasedContent();
      }
    }

    ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
      clearTimeout(this.profileTipTimer);
      clearTimeout(this.refreshTimer);
      this.stopDragListeners();
    }

    // ---------- Profile: BMI category chip ----------

    private get bmiValue(): number {
      const v = parseFloat(this.profileData?.bmi);
      return isFinite(v) && v > 0 ? v : 0;
    }
    get bmiLabel(): string {
      const api = String(this.profileData?.bmiStatus ?? '').trim();
      if (api) return api;                          // dashboard API value, shown as returned
      if (this.dashboardProfileApplied) return '';  // API sent no status: do not calculate one on the frontend
      const v = this.bmiValue;
      if (!v) return '';
      if (v < 18.5) return 'Underweight';
      if (v < 25) return 'Normal';
      if (v < 30) return 'Overweight';
      return 'Obese';
    }
    get bmiKey(): string {
      return this.bmiLabel.toLowerCase().replace(/[^a-z]/g, '');   // 'OBESE' -> 'obese' (matches .hd-bmi--obese)
    }

    // ---------- Info cards (built from the counts you already load) ----------

    /** Rebuilt only when a count changes, so the array identity is stable between change-detection passes. */
    get cards(): DashCard[] {
      const key = [this.medicationCount, this.insurance, this.careplan, this.medcondition,
                   this.immunization, this.allergies, this.famhistory, this.devicecount,
                   this.isLoadingProfile, this.dashboardError, this.devicesLoaded, this.deviceError,
                   this.documentsLoading, this.documentsError,
                   JSON.stringify(this.deviceRecord || ''), JSON.stringify(this.allergyRecords),
                   JSON.stringify(this.documentRecords), this.healthDocs?.created.length || 0,
                   JSON.stringify([this.medicationList, this.vitalList, this.familyList, this.insuranceList, this.immunizationList])].join('|');
      if (key !== this.cardsKey) {
        this.cardsKey = key;
        this.cardsCache = this.buildCards().filter(c => DASHBOARD_SECTIONS[c.id]);
      }
      return this.cardsCache;
    }

    trackCard(_index: number, card: DashCard): string { return card.id; }
    trackSlide(index: number): number { return index; }

    private buildCards(): DashCard[] {
      const num = (v: any): number => Number(v) || 0;
      const make = (c: {
        id: string; title: string; image: string; tint: DashCard['tint']; route: string | null;
        addCategory: string | null; footIcon: string; count: number; one: string; many: string;
        emptyText: string; filledText: string; addTitle: string; addText: string;
        footLabel?: string; footValue?: string; canAdd?: boolean; icon?: string;
        /** Latest record, once an API provides one. Never filled with sample data. */
        record?: DashRecord;
        /** Several records (one panel each, first MAX_DASH_RECORDS). Takes precedence over `record`. */
        records?: DashRecord[];
        status?: 'loading' | 'error';
        /** Text of the "+ Add ..." action shown when the section has no data */
        actionLabel?: string;
        /** Second line of the empty state (default 'Nothing added yet') */
        emptySubtitle?: string;
      }): DashCard => {
        const label = c.footLabel || 'Total';
        const recs = (c.records && c.records.length ? c.records : (c.record ? [c.record] : [])).slice(0, MAX_DASH_RECORDS);
        const toSlide = (r: DashRecord): DashSlide => ({
          kind: 'summary', title: r.title, subtitle: r.subtitle, footLabel: r.footLabel || label, footValue: r.footValue,
          footTone: r.footTone, docId: r.docId, note: r.note, icon: r.icon });
        let summary: DashSlide;
        if (c.status === 'loading') {
          summary = { kind: 'summary', title: 'Loading', subtitle: 'Loading records', footLabel: label, footValue: '00' };
        } else if (c.status === 'error') {
          summary = { kind: 'summary', title: 'Unable to load', subtitle: 'Please try again later', footLabel: label, footValue: '—' };
        } else if (recs.length) {
          summary = toSlide(recs[0]);
        } else if (c.count > 0) {
          // only a count is available today: show it (real data), the record layout comes with the updated API
          summary = { kind: 'summary', title: `${c.count} ${c.count === 1 ? c.one : c.many}`, subtitle: c.filledText, footLabel: 'Total', footValue: String(c.count) };
        } else {
          // no data: same record container, with the message and the add action (never sample data)
          summary = { kind: 'empty', title: c.emptyText, subtitle: c.emptySubtitle || 'Nothing added yet', footLabel: label, footValue: '—',
                      actionLabel: c.actionLabel || c.addTitle };
        }
        const slides: DashSlide[] = [summary];
        if (!c.status) { recs.slice(1).forEach(r => slides.push(toSlide(r))); }   // remaining records, one panel each
        if (!c.status && summary.kind !== 'empty' && c.addCategory && c.canAdd !== false) {
          slides.push({ kind: 'add', title: c.addTitle, subtitle: c.addText, footLabel: 'Quick action', footValue: '+ Add' });
        }
        return { id: c.id, title: c.title, image: c.image, tint: c.tint, route: c.route,
                 addCategory: c.addCategory, footIcon: c.footIcon, icon: c.icon, status: c.status,
                 addTip: ADD_TIPS[c.id], canAddRecord: !!c.addCategory, slides };
      };
      const added = 'Added to your profile';
      const devices = num(this.devicecount);
      const shownDevices = THIRD_PARTY_INTEGRATIONS_LIVE ? devices : 0;   // no integration yet: Connected Health shows its empty state
      // Medications / Vitals / Insurance / Immunizations / Allergies / Family History are fed by GET consumer/dashboard/
      const dst: 'loading' | 'error' | undefined =
        this.isLoadingProfile ? 'loading' : (this.dashboardError ? 'error' : undefined);
      // Health Documents is fed by GET consumer/v3/dashboard/documents?tag=health (its own loading / error state)
      const docSt: 'loading' | 'error' | undefined =
        this.documentsLoading ? 'loading' : (this.documentsError ? 'error' : undefined);
      // Connected Health is fed by the existing device_list() API
      const devSt: 'loading' | 'error' | undefined = !THIRD_PARTY_INTEGRATIONS_LIVE ? undefined :
        ((!this.devicesLoaded && !this.deviceError) ? 'loading' : ((this.deviceError && !devices) ? 'error' : undefined));

      return [
        make({ id: 'medication', title: 'Medications', image: 'assets/images/medicine.png', icon: 'pill', tint: 'blue',
               route: 'medication', addCategory: 'medication', footIcon: 'clock', footLabel: 'Next Dose', status: dst,
               records: this.medicationRecords,
               count: this.medicationList ? this.medicationList.length : num(this.medicationCount), one: 'medication', many: 'medications',
               emptyText: 'No medication available', filledText: added,
               addTitle: 'Add medication', addText: 'Log a new medication', actionLabel: 'Add Medication' }),
        // Fed by data.vitals of GET consumer/dashboard/. View all = existing Manual Devices screen (/health/devices).
        make({ id: 'vitals', title: 'Vitals', image: '', icon: 'heart', tint: 'blue',
               route: 'devices', addCategory: 'vitals', footIcon: 'pulse', footLabel: 'Status', status: dst,
               records: this.vitalRecords,
               count: this.vitalList.length, one: 'vital reading', many: 'vital readings',
               emptyText: 'No vitals available', filledText: added,
               addTitle: 'Add vital', addText: 'Log a new reading', actionLabel: 'Add Vital' }),
        make({ id: 'documents', title: 'Health Documents', image: '', icon: 'doc', tint: 'blue',
               route: null, addCategory: 'documents', footIcon: 'calendar', footLabel: 'Uploaded',
               status: docSt, records: this.documentRecords,
               count: this.healthDocs?.created.length || 0, one: 'document', many: 'documents',
               emptyText: 'No documents available', filledText: added,
               addTitle: 'Add document', addText: 'Upload a health document', actionLabel: 'Add Document' }),
        make({ id: 'insurance', title: 'Insurance', image: 'assets/images/health/icons/insurance-shield.svg', icon: 'insurance', tint: 'blue',
               route: 'profile/insurance', addCategory: 'insurance', footIcon: 'pulse', footLabel: 'Status', status: dst,
               records: this.insuranceRecords,
               count: num(this.insurance), one: 'insurance', many: 'insurances',
               emptyText: 'No insurance available', filledText: added,
               addTitle: 'Add insurance', addText: 'Save a new policy', actionLabel: 'Add Insurance' }),
        make({ id: 'immunization', title: 'Immunizations', image: 'assets/images/health/healthlogico/addimmu.svg', icon: 'syringe', tint: 'green',
               route: 'profile/immunization', addCategory: 'immunization', footIcon: 'calendar', footLabel: 'Given on', status: dst,
               records: this.immunizationRecords,
               count: num(this.immunization), one: 'immunization', many: 'immunizations',
               emptyText: 'No immunizations available', filledText: added,
               addTitle: 'Add immunization', addText: 'Record a new vaccine', actionLabel: 'Add Immunization' }),
        make({ id: 'allergy', title: 'Allergies', image: 'assets/images/health/icons/allergies 1(1).svg', icon: 'allergy', tint: 'orange',
               route: 'profile/allergy', addCategory: 'allergy', footIcon: 'alert', footLabel: 'Severity', status: dst, records: this.allergyRecords,
               count: num(this.allergies), one: 'allergy', many: 'allergies',
               emptyText: 'No allergies available', filledText: added,
               addTitle: 'Add allergy', addText: 'Record a new allergy', actionLabel: 'Add Allergy' }),
        make({ id: 'familyhistory', title: 'Family History', image: 'assets/images/health/healthlogico/familyhispng.png', icon: 'users', tint: 'purple',
               route: 'profile/familyhistory', addCategory: 'familyhistory', footIcon: 'users', footLabel: 'Relation', status: dst,
               records: this.familyRecords,
               count: num(this.famhistory), one: 'record', many: 'records',
               emptyText: 'No family history available', filledText: added,
               addTitle: 'Add family history', addText: 'Record a family member', actionLabel: 'Add Family History' }),
        make({ id: 'devices', title: 'Connected Health', image: 'assets/images/weight-loss.png', icon: 'phone', tint: 'blue',
               route: 'devices', addCategory: 'device', footIcon: 'clock',
               count: shownDevices, one: 'device', many: 'devices',
               emptyText: 'No connected apps', emptySubtitle: 'Connect a health app to sync.',
               filledText: 'Connected to your account',
               addTitle: 'Sync App ', addText: 'Connect a health app', actionLabel: 'Connect App',
               footLabel: 'Status', record: this.deviceRecord, status: devSt }),
        // ---- Hidden for now (see DASHBOARD_SECTIONS) ----
        make({ id: 'careplan', title: 'Care Plan', image: 'assets/images/careplan.png', tint: 'blue',
               route: 'care-plan', addCategory: null, footIcon: 'pulse',
               count: num(this.careplan), one: 'care plan', many: 'care plans',
               emptyText: 'No targets added yet...', filledText: 'Targets and goals',
               addTitle: '', addText: '' }),
        make({ id: 'medicalcondition', title: 'Medical Conditions', image: 'assets/images/health/icons/med-rep.png', tint: 'blue',
               route: 'profile/medicalcondition', addCategory: 'medicalcondition', footIcon: 'doc',
               count: num(this.medcondition), one: 'condition', many: 'conditions',
               emptyText: 'No medical condition(s) added yet...', filledText: added,
               addTitle: 'Add condition', addText: 'Record a medical condition' })
      ];
    }

    openCard(card: DashCard, event?: Event): void {
      if (card.id === 'documents') { this.openDocuments(); return; }   // View all -> Documents > Health
      if (!card.route) { return; }                 // UI-only card: nothing to open yet
      this.checkHealthUpdates(card.route, event?.currentTarget as HTMLElement | undefined);
    }

    onSlideActivate(event: Event, card: DashCard, slide: DashSlide): void {
      if (this.suppressClick) { return; }          // the click that ends a mouse drag
      if (card.id === 'documents' && slide.kind === 'summary') {
        this.openDocuments({ docId: slide.docId });  // opens the Documents module's own preview
        return;
      }
      if ((slide.kind === 'add' || slide.kind === 'empty') && card.addCategory) {
        this.handlePlusClick(event, card.addCategory);    // existing Add flow (same dialogs + profile check)
      } else if (slide.kind === 'empty') {
        return;                                            // no add flow yet: nothing to open on an empty card
      } else if (card.route) {
        this.checkHealthUpdates(card.route, event.currentTarget as HTMLElement | undefined);
      }
    }

    // ---------- Carousel behaviour ----------

    activeIndex(card: DashCard): number {
      return Math.min(this.slideIndex[card.id] || 0, card.slides.length - 1);
    }

    private slideStep(el: HTMLElement): number {
      return el.children.length > 1
        ? (el.children[1] as HTMLElement).offsetLeft - (el.children[0] as HTMLElement).offsetLeft
        : el.clientWidth;
    }

    private currentIndex(el: HTMLElement): number {
      const step = this.slideStep(el) || 1;
      return Math.max(0, Math.min(el.children.length - 1, Math.round(el.scrollLeft / step)));
    }

    private prefersReducedMotion(): boolean {
      return typeof window !== 'undefined' && !!window.matchMedia &&
             window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /** Keeps dots and arrows in sync with every input: swipe, wheel, drag, arrows, keyboard. */
    trackScroll(card: DashCard, el: HTMLElement): void {
      const i = this.currentIndex(el);
      if (this.slideIndex[card.id] !== i) { this.slideIndex[card.id] = i; }
    }

    private goToSlide(el: HTMLElement, index: number): void {
      const i = Math.max(0, Math.min(el.children.length - 1, index));
      el.scrollTo({ left: i * this.slideStep(el), behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    scrollSlide(el: HTMLElement, delta: number): void {
      this.goToSlide(el, this.currentIndex(el) + delta);
    }

    onTrackKey(event: Event, el: HTMLElement, delta: number): void {
      if (el.children.length < 2) { return; }
      event.preventDefault();
      this.scrollSlide(el, delta);
    }

    onTrackEnter(event: Event, card: DashCard, el: HTMLElement): void {
      if (event.target !== el) { return; }
      const slide = card.slides[this.currentIndex(el)];
      if (slide) {
        event.preventDefault();
        this.onSlideActivate(event, card, slide);
      }
    }

    /** Mouse drag-to-swipe. Touch and trackpad use native scroll-snap. */
    onTrackPointerDown(event: Event, el: HTMLElement): void {
      const e = event as PointerEvent;
      if (e.pointerType !== 'mouse' || e.button !== 0 || el.children.length < 2) { return; }
      this.drag = { el, startX: e.clientX, startScroll: el.scrollLeft, startIndex: this.currentIndex(el), moved: false };
      el.classList.add('is-dragging');
      this.zone.runOutsideAngular(() => {           // no change detection while the mouse moves
        window.addEventListener('pointermove', this.onDragMove);
        window.addEventListener('pointerup', this.onDragEnd);
        window.addEventListener('pointercancel', this.onDragEnd);
      });
    }

    private onDragMove = (e: PointerEvent): void => {
      const d = this.drag;
      if (!d) { return; }
      const dx = e.clientX - d.startX;
      if (Math.abs(dx) > 4) { d.moved = true; }
      d.el.scrollLeft = d.startScroll - dx;
    };

    private onDragEnd = (e: PointerEvent): void => {
      const d = this.drag;
      if (!d) { return; }
      this.stopDragListeners();
      d.el.classList.remove('is-dragging');
      const dx = e.clientX - d.startX;
      if (d.moved) {
        this.suppressClick = true;                  // swallow the click that follows a drag
        setTimeout(() => (this.suppressClick = false), 0);
      }
      this.zone.run(() => this.goToSlide(d.el, d.startIndex + (dx < -40 ? 1 : dx > 40 ? -1 : 0)));
    };

    private stopDragListeners(): void {
      window.removeEventListener('pointermove', this.onDragMove);
      window.removeEventListener('pointerup', this.onDragEnd);
      window.removeEventListener('pointercancel', this.onDragEnd);
      this.drag = null;
    }

    @HostListener('window:resize')
    onResize(): void {
      this.trackRefs?.forEach(ref => {
        const el = ref.nativeElement;
        const i = this.slideIndex[el.getAttribute('data-card') ?? ''] || 0;
        el.scrollTo({ left: i * this.slideStep(el), behavior: 'auto' });
      });
    }

    // ---------- Add Record menu (desktop button + round mobile button) ----------

    /** Both "Add Record" buttons (desktop button + round phone button) call this: it opens the modal. */
    toggleAddMenu(event: Event): void {
      event.stopPropagation();
      if (this.addRecordRef) { return; }
      this.addMenuOpen = true;
      // MatDialog gives the dark backdrop, focus trap, Escape and click-outside to close, like the other dialogs
      this.addRecordRef = this.dialog.open(this.addMenuTpl, {
        panelClass: 'hd-add-dialog',
        backdropClass: 'hd-add-backdrop',
        width: '512px',
        maxWidth: '94vw',
        ariaLabelledBy: 'hd-ar-title'
      });
      this.addRecordRef.afterClosed().subscribe(() => { this.addRecordRef = null; this.addMenuOpen = false; });
    }

    closeAddRecord(): void { this.addRecordRef?.close(); }

    /**
     * Samsung Health is not integrated yet. This dialog only informs: it starts no sign-in, sync or API call,
     * creates no device record and never shows a connected state. Used by the Connected Health card and by the
     * Add Record modal, which stays open underneath so the user returns to where they were.
     */
    openComingSoon(): void {
      if (this.comingSoonRef) { return; }
      this.comingSoonRef = this.dialog.open(this.comingSoonTpl, {
        panelClass: ['hd-add-dialog', 'hd-cs-dialog'],   // same rounded panel as Add Record
        backdropClass: 'hd-add-backdrop',
        width: '420px',
        maxWidth: '94vw',
        ariaLabelledBy: 'hd-cs-title',
        ariaDescribedBy: 'hd-cs-desc'
      });
      this.comingSoonRef.afterClosed().subscribe(() => { this.comingSoonRef = null; });
    }

    closeComingSoon(): void { this.comingSoonRef?.close(); }

    onAddRecord(event: Event, category: string): void {
      if (category === 'device') {            // Samsung Health: Coming Soon on top of Add Record (which stays open)
        event.stopPropagation();
        this.openComingSoon();
        return;
      }
      if (this.profileIncomplete) {           // modal card disappears with the modal, so anchor the tip to the page's Add Record button
        event.stopPropagation();
        this.closeAddRecord();
        this.showProfileTip(this.visibleAddButton());
        return;
      }
      this.closeAddRecord();
      this.handlePlusClick(event, category);        // existing handler: same dialogs and profile check as before
      // Vitals opens its own Add Vitals dialog now, so only Health Documents still says "coming soon"
      if (category === 'documents' &&
          this.profileData?.weight && this.profileData?.height && this.profileData?.bloodGroup) {
        this.toastr.info('Coming soon');
      }
    }

    onEditHealth(): void {
      if (this.profileDialogRef) { return; }   // never stack a second Health Info dialog
      this.hideProfileTip();
      const dialogRef = this.dialog.open(HealthEditDialogComponent, {
        width: '470px',
        panelClass: 'custom-dialog-container',
        data: this.profileData
      });
      this.profileDialogRef = dialogRef;

      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
        this.profileDialogRef = null;
        // Plain refresh only: closing (Cancel / X / browser back) must NOT open the dialog again.
        this.getProfile();
        console.log('Updated Health Info:', result);
      });
      // if the dashboard is left while the dialog is open, forget the reference (the dialog itself closes on navigation)
      this.destroy$.subscribe(() => { this.profileDialogRef = null; });
    }
  


    OnMedication(){
      this.showMedication = true;
    }
  
    goToCarePlan(): void {
      this.showCarePlan = true;
    }
  
    backToHealth(): void {
      this.showCarePlan = false;
      this.showMedication = false;
    }

    getActivityLog(){
      const formatted = format(new Date(), 'yyyyMMdd');

      this.auth.get_activity_feed_tilldate(formatted).subscribe((res: any) => {
        const isoString = res?.data?.activitylog?.[0]?.activityDate;
        if (!isoString) { return; }
        const d = new Date(isoString);
        if (!isNaN(d.getTime())) { this.lastUpdated = format(d, 'MMM d, yyyy'); }
      }, () => {})
    }
    
    /** Latest connected device from the existing device_list() API. CONFIRM the field names against the real response. */
    get deviceRecord(): { title: string; subtitle: string; footValue: string } | undefined {
      if (!THIRD_PARTY_INTEGRATIONS_LIVE) { return undefined; }
      const d = this.deviceList[0];
      if (!d) { return undefined; }
      const name = d.deviceName || d.device_name || d.name || d.deviceType || d.provider;
      if (!name) { return undefined; }
      const displayName = String(name).toLowerCase().includes('weight') ? 'Samsung Health' : String(name);
      const status = d.status || d.syncStatus || d.connectionStatus;
      return { title: displayName, subtitle: 'Connected device', footValue: status ? String(status) : '—' };
    }

    /** Allergies (data.allergies): allergen, "Allergen", severity. The reaction list stays in the Allergy screen. */
    get allergyRecords(): DashRecord[] {
      return this.allergyList.filter(a => a.allergicTo).map(a => ({
        title: a.allergicTo, subtitle: 'Allergen', footValue: a.severity || '—'
      }));
    }

    /** data.medications: one record per medication (name, 'strength unit', time). The internal id is not shown. */
    get medicationRecords(): DashRecord[] {
      return (this.medicationList || []).filter(m => m.name).map(m => ({
        title: m.name, subtitle: m.dose || 'Medication', footLabel: 'Time', footValue: m.time || '—'
      }));
    }

    /**
     * data.vitals, one record per reading: the reading, "<name> · <date>", and Status (empty risk = Normal).
     * The API message stays available as the tooltip of the record.
     */
    get vitalRecords(): DashRecord[] {
      return this.vitalList.filter(v => v.reading).map(v => {
        const date = this.shortDate(v.readingDateTime, 'MM-dd-yyyy hh:mm a');
        return {
          title: v.reading,
          subtitle: [v.name, date].filter(s => s).join(' · '),
          footValue: v.risk ? v.risk.charAt(0) + v.risk.slice(1).toLowerCase() : 'Normal',
          footTone: (v.risk === 'CRITICAL' || v.risk === 'HIGH') ? 'danger' as const : undefined,
          note: v.msg || undefined,
          icon: this.vitalIcon(v.name)
        };
      });
    }

    /** Same device as the Add Vitals dialog (glucose, oxygen, blood pressure, weight, temperature), drawn as an outline. Unknown reading = the card's heart. */
    private vitalIcon(name: string): string | undefined {
      const n = (name || '').toLowerCase();
      if (n.includes('glucose')) { return 'drop'; }
      if (n.includes('oxygen')) { return 'lungs'; }
      if (n.includes('pressure')) { return 'heartpulse'; }
      if (n.includes('weight')) { return 'scale'; }
      if (n.includes('temp')) { return 'thermo'; }
      return undefined;
    }

    /** data.familyHistory: condition, "Family condition", and the relationship(s) as text (never the raw array). */
    get familyRecords(): DashRecord[] {
      return this.familyList.filter(f => f.conditionName).map(f => ({
        title: f.conditionName,
        subtitle: 'Family condition',
        footValue: f.relationships.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ') || '—'
      }));
    }

    /** data.insurance: plan name, company and status (policy / member / group numbers stay in the Insurance screen). */
    get insuranceRecords(): DashRecord[] {
      return this.insuranceList.filter(i => i.planName || i.company).map(i => ({
        title: i.planName || i.company,
        subtitle: i.planName ? (i.company || 'Insurance') : 'Insurance',
        footValue: i.status || '—'
      }));
    }

    /** data.immunization: vaccine, "Immunization", and the administered date. */
    get immunizationRecords(): DashRecord[] {
      return this.immunizationList.filter(v => v.vaccineName).map(v => ({
        title: v.vaccineName,
        subtitle: 'Immunization',
        footValue: this.shortDate(v.adminDate, 'MM-dd-yyyy') || '—'
      }));
    }

    /** API dates ('MM-dd-yyyy' / 'MM-dd-yyyy hh:mm a', the format the device screens parse) as 'Aug 2, 2026'. Falls back to the raw text. */
    private shortDate(raw: string, pattern: string): string {
      if (!raw) { return ''; }
      const d = parse(raw, pattern, new Date());
      return isNaN(d.getTime()) ? raw : format(d, 'MMM d, yyyy');
    }

    /** Document dates: a real date (MM-dd-yyyy, MM-dd-yyyy hh:mm a, yyyy-MM-dd) becomes 'Oct 12, 2026'; relative text such as '2 days ago.' stays as the API returned it. */
    private formatApiDate(raw: string): string {
      const r = (raw || '').trim();
      if (/^\d{2}-\d{2}-\d{4}$/.test(r)) { return this.shortDate(r, 'MM-dd-yyyy'); }
      if (/^\d{2}-\d{2}-\d{4} \d{1,2}:\d{2} [AP]M$/i.test(r)) { return this.shortDate(r, 'MM-dd-yyyy hh:mm a'); }
      if (/^\d{4}-\d{2}-\d{2}/.test(r)) { return this.shortDate(r.slice(0, 10), 'yyyy-MM-dd'); }
      return r;
    }

    /**
     * Health Documents (citizen_primary only), one record per document: expiring documents first
     * ("Expiring on <value>" in red), then the created documents that are not expiring ("Uploaded <created>").
     * The API returns no description / file type, so the second line is the category and the primary country.
     */
    get documentRecords(): DashRecord[] {
      const d = this.healthDocs;
      if (!d) { return []; }
      const subtitle = d.country ? `Health · ${d.country}` : 'Health document';
      const expiringIds = new Set(d.expiring.map(x => x.id));
      return [
        ...d.expiring.map(x => ({ title: x.name, subtitle, docId: x.id, footLabel: 'Expiring on', footValue: this.formatApiDate(x.expiration) || '—', footTone: 'danger' as const })),
        ...d.created.filter(x => !expiringIds.has(x.id)).map(x => ({ title: x.name, subtitle, docId: x.id, footValue: this.formatApiDate(x.created) || '—' }))
      ];
    }

    /** Shows the full text as a tooltip only when the text is actually truncated (native tooltip, no new library). */
    tipIfTruncated(ev: Event): void {
      const el = ev.currentTarget as HTMLElement;
      if (el.scrollWidth > el.clientWidth) { el.setAttribute('title', (el.textContent || '').trim()); }
      else { el.removeAttribute('title'); }
    }

    getHealthDocuments(){
      this.documentsLoading = true;
      this.documentsError = false;
      this.auth.getDashboardDocuments('health').subscribe((res: any) => {
        const view = mapHealthDocuments(res as DashboardDocumentsResponse);
        this.healthDocs = view;
        this.documentsError = !view;
        this.documentsLoading = false;
        this.cdr.detectChanges();
      }, () => {
        this.healthDocs = null;
        this.documentsError = true;
        this.documentsLoading = false;
        this.cdr.detectChanges();
      });
    }

    /**
     * Deep link into the existing Documents module. The Documents page selects Health, uses the existing
     * ?country= query param for the citizen_primary country, and then (optionally) opens the Add Documents
     * dialog or the preview of one document. Router state (not the URL) carries the request.
     */
    private openDocuments(opts: { docId?: string; add?: boolean } = {}): void {
      const country = this.healthDocs?.country;
      this.router.navigate(['/documents'], {
        queryParams: country ? { country } : undefined,
        state: { dashboardDeepLink: { category: 'health', ...opts } }
      });
    }

    getDevices(){
      this.auth.device_list().subscribe((res:any) =>{
        this.deviceList = res?.data?.device || [];
        this.devicecount = this.deviceList.length;
        this.devicesLoaded = true;
        this.deviceError = false;
        this.cdr.detectChanges();
      }, () => {
        this.deviceError = true;
        this.cdr.detectChanges();
      })
    }
    getCarePlan(){
      this.auth.get_care_plan().subscribe((res:any) =>{
        this.careplan= res.data.careplan.length;
        this.careplanid = res.data.careplan[0]?.id;
        console.log('careplan',this.careplan);
        console.log('careplanid',this.careplanid);
        // this.getGoals(this.careplanid)
      })
    }

  getGoals(id: any) {
    this.auth.get_careplangoal(id).subscribe((res: any) => {
      let totalList = res.data.goal || [];
      this.totalGoals = totalList.length; // Get the count
    })
  }


    getMedication(){
      this.auth.profile_details_cat('medication').subscribe((res:any) =>{ 
        this.medication = res.data?.medication;
        //console.log('Medication List:', this.medication);
      }, error => {
        //console.error('Error fetching medication list:', error);    
      }
      );
    }

    getCountLogs(){

      this.auth.profile_details_cat('medication').subscribe((res:any) =>{   
        this.medicationCount = res.data?.medication?.length || 0;
        this.cdr.detectChanges();
        //console.log('Medication Count List:', this.medication);
      });
      this.auth.profile_details_cat('insurance').subscribe((res: any) => {
        this.insurance = res.data?.insurance?.length || 0;
        this.cdr.detectChanges();
        //console.log('Insurance Count List:', this.insurance);
      });
      this.auth.profile_details_cat('familyhistory').subscribe((res: any) => {
        this.famhistory = res.data?.familyHistory?.length || 0;
        this.cdr.detectChanges();
        //console.log('Family History Count List:', this.famhistory);
      });
      this.auth.profile_details_cat('medicalcondition').subscribe((res: any) => {
        this.medcondition = res.data?.medicalCondition?.length || 0;
        this.cdr.detectChanges();
        //console.log('Medical Condition Count List:', this.medcondition);
      });
      this.auth.profile_details_cat('allergy').subscribe((res: any) => {
        this.allergies = res.data?.allergy?.length || 0;
        this.cdr.detectChanges();
        //console.log('Allergy Count List:', this.allergies);
      });
      this.auth.profile_details_cat('immunization').subscribe((res: any) => {
        this.immunization = res.data?.immunization?.length || 0;
        this.cdr.detectChanges();
        //console.log('Immunization Count List:', this.immunization);
      });
    }

    /** One call: GET consumer/dashboard/ feeds the Profile card and every record card (no separate profile / profile-picture calls). */
    getProfile(initialLoad = false) {
      this.isLoadingProfile = true;
      this.dashboardError = false;
      this.pageLoader = initialLoad;   // the full-screen spinner is for the first load only (not for the refresh after the dialog closes)

      this.profileImg = localStorage.getItem('profileImg') || '';
      // takeUntil: a response that arrives after the user left the dashboard (browser back) is dropped,
      // so it can never open the Health Info dialog on top of another screen.
      this.auth.get_consumer_dashboard().pipe(catchError(() => of(null)), takeUntil(this.destroy$)).subscribe((r: any) => {
        this.pageLoader = false;
        const view: DashboardView | null = mapDashboardResponse(r as DashboardResponse);
        this.dashboardError = !view;
        if (!view) {
          this.isLoadingProfile = false;   // API failed: stop the spinner and show the error state on the cards
          return;
        }
        this.profileData = view.profile;   // height, weight (weigthtUnit mapped), BMI, BMI status, blood group, DOB
        this.name = `${localStorage.getItem('first_name') || ''} ${localStorage.getItem('last_name') || ''}`.trim();

        // counts come from the returned arrays (empty array = empty state, not an error)
        this.dashboardProfileApplied = true;
        this.allergyList = view.allergies;
        this.medicationList = view.medications;
        this.vitalList = view.vitals;
        this.familyList = view.familyHistory;
        this.insuranceList = view.insurance;
        this.immunizationList = view.immunization;
        this.insurance = view.insuranceCount;
        this.immunization = view.immunizationCount;
        this.allergies = view.allergyCount;
        this.famhistory = view.familyHistoryCount;
        this.isLoadingProfile = false; // stop loading once data is ready

        // New account: ask for the health profile once, when the dashboard opens. Cancel / X just closes it.
        if (initialLoad && this.profileIncomplete) {
          this.onEditHealth();
        }
      })
    }

    getInitials(name: string): string {
      //console.log('name',name);
      if (!name) return '';
      const parts = name.trim().split(' ').filter(p => p); // remove empty
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return parts.slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('');
    }
  
    showUnits(){
       if(this.country== "usa" || this.country == "united states of america"||this.country=="USA"){
        this.height ="feet"
        var show = true
        this.weight="lbs"
  
      } else {
        this.height ="cm"
        this.weight="kg"
  
      }
    }

    // Navigate to Health Log screen
    onHealthLogClick(): void {
      console.log("=== HEALTH LOG NAVIGATION DEBUG ===")
      console.log("Current route before navigation:", this.router.url)
      console.log("Attempting to navigate to: /health/health-log")

      // Direct navigation to health log
      this.router
        .navigate(["/health/health-log"])
        .then((success) => {
          console.log("Navigation result:", success)
          console.log("New route after navigation:", this.router.url)
          if (success) {
            console.log("Successfully navigated to Health Log")
            // Optional: Show success message
            // this.toastr.success("Loading Health Log...", "Navigation")
          } else {
            console.error("Navigation failed - route might not exist")
            this.toastr.error("Failed to navigate to Health Log", "Navigation Error")
          }
        })
        .catch((error) => {
          console.error("Navigation error:", error)
          this.toastr.error("Error navigating to Health Log", "Navigation Error")
        })
    }

    // Alternative navigation method (fallback)
    navigateToHealthLogAlternative(): void {
      // This bypasses any programmatic navigation issues
      //console.log("Using alternative navigation method")
      window.location.href = "/health/health-log"
    }

    openCategoryDialog(category: string){
        const dialogRef = this.dialog.open(AllergyDialogComponent, {
          width: '600px',
          maxWidth: '95vw',
          data: { 
            mode: 'add',
            selected: category
          },
          disableClose: true,
          autoFocus: true,
        
        });
    
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            // this.getCountLogs();
            this.router.navigate([`/health/profile/${category}`]);
          }
        });
    }


    openMedicationDialog(){
        const dialogRef = this.dialog.open(MedicationDialogComponent, {
          width: '500px',
          height: '500px',
          data: { 
            mode: 'add'
          },
          disableClose: true,
          autoFocus: true,
        
        });
    
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            // this.getCountLogs()
            this.router.navigate(['/health/medication'])
          }
        });
       }

    openDeviceDialog(){
        const dialogRef = this.dialog.open(AddDevicesComponent, {
          width: '500px',
          data: { 
            mode: 'add'
          },
          disableClose: true,
          autoFocus: true,
        
        });
    
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            // this.getDevices();
            this.router.navigate(['/health/devices']);
          }
        });
       }

    /** Add Vitals / Vitals "+": device cards + reading form in one modal. (openDeviceDialog above stays for Connected Health.) */
    openVitalsDialog(){
        this.dialog.open(AddVitalsComponent, {
          width: '600px',
          maxWidth: '95vw',
          disableClose: true,
          autoFocus: false,
        });
    }

    checkHealthUpdates(cat: any, anchor?: HTMLElement | null) {
      // console.log('Check Health Updates for:', cat);
      // console.log('+ Profile Data:', this.profileData);
      // console.log('careplanid:', this.careplanid);
      if (this.profileIncomplete) {
        this.showProfileTip(anchor);
        return;
      }
      else {
        if (cat == 'care-plan') {
          this.router.navigate([`/health/${cat}/${this.careplanid}`]);
        }
        else {
          this.router.navigate([`/health/${cat}`]);
        }
      }
    }

    handlePlusClick(event: Event, category: string): void {
      event.stopPropagation(); // stop the outer card's routerLink

      // Connected Health / Connect App: Samsung Health is coming soon. Nothing is connected, and this does not need the profile check.
      // (The old 'device' case below, with openDeviceDialog(), is intentionally left in place but no longer reached.)
      if (category === 'device') {
        this.openComingSoon();
        return;
      }

      //console.log('+ Profile Data:', this.profileData);
      if (this.profileIncomplete) {
        this.showProfileTip(event.currentTarget as HTMLElement | null);
        return;
      }

      switch (category) {
        case 'vitals':
          this.openVitalsDialog();   // device cards + reading form in one modal
          break;
        case 'documents':
          this.openDocuments({ add: true });   // Documents > Health > Add Documents dialog (Health preselected)
          break;
        case 'insurance':
        case 'allergy':
        case 'immunization':
        case 'medicalcondition':
        case 'familyhistory':
          this.openCategoryDialog(category);
          break;
        case 'medication':
          this.openMedicationDialog();
          break;

        case 'device':
          if (this.devicecount >= 5) {
            // navigate instead of opening dialog
            this.router.navigate(['/health/devices']);
          } else {
            // open dialog
            this.openDeviceDialog()
          }
          break;

        default:
          break;
      }
    }

    // ---------- "Complete your health profile first" tip ----------

    /** Same three fields the add / view-all actions already required. */
    get profileIncomplete(): boolean {
      return !this.profileData?.weight || !this.profileData?.height || !this.profileData?.bloodGroup;
    }

    /** The Add Record button that is actually visible (desktop button or the round phone button). */
    private visibleAddButton(): HTMLElement | null {
      const els = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('.hd-add-btn, .hd-fab'));
      return els.find(el => el.getBoundingClientRect().width > 0) || null;
    }

    /** Small popover next to the clicked add action (below it, or above when there is no room). Falls back to the top centre. */
    showProfileTip(anchor?: HTMLElement | null): void {
      const vw = window.innerWidth, vh = window.innerHeight, margin = 8;
      const width = Math.min(300, vw - margin * 2);
      const r = anchor?.getBoundingClientRect();
      if (r && (r.width > 0 || r.height > 0)) {
        const cx = r.left + r.width / 2;
        const left = Math.max(margin, Math.min(cx - width / 2, vw - width - margin));
        const arrow = Math.max(18, Math.min(cx - left, width - 18));
        const below = r.bottom + 12 + 150 <= vh || r.top < 170;     // ~150px = tip height
        this.profileTip = below
          ? { left, width, top: r.bottom + 12, bottom: null, arrow }
          : { left, width, top: null, bottom: vh - r.top + 12, arrow };
      } else {
        this.profileTip = { left: Math.max(margin, (vw - width) / 2), width, top: 80, bottom: null, arrow: width / 2 };
      }
      this.profileTipOpenedAt = Date.now();
      clearTimeout(this.profileTipTimer);
      this.profileTipTimer = setTimeout(() => this.hideProfileTip(), 7000);
    }

    hideProfileTip(): void {
      clearTimeout(this.profileTipTimer);
      this.profileTip = null;
    }

    /** "Complete profile" button of the tip. */
    completeProfileFromTip(): void {
      this.hideProfileTip();
      this.onEditHealth();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClickForTip(e: Event): void {
      if (!this.profileTip || Date.now() - this.profileTipOpenedAt < 200) { return; }   // the click that opened it
      if ((e.target as HTMLElement | null)?.closest?.('.hd-ptip')) { return; }
      this.hideProfileTip();
    }

    @HostListener('document:keydown.escape')
    @HostListener('document:wheel')
    @HostListener('document:touchmove')
    @HostListener('window:resize')
    onTipDismissEvents(): void {
      if (this.profileTip) { this.hideProfileTip(); }
    }

    openAlertDialog(): void {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: { profData: this.profileData },
        width: '450px',
        height: '185px',
        hasBackdrop: true,
        panelClass: 'hello',
        disableClose: true
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          //console.log(result)
          // if(result){
          //   this.type=result
          //   localStorage.setItem('emailType',result)
          //   this.emailAuthentication(result)
          // }           
        }
      });
    }

}


@Component({
    selector: 'app-confirmation-dialog',
    template: `
    <div mat-dialog-content class="custom-dialog">
      <h2 class="dialog-title">Alert!</h2>
        <p class="dialog-message">
          To continue, please complete your health profile update.
        </p>
      <hr class="dialog-divider" />
      <div mat-dialog-actions class="dialog-actions">
        <button mat-button class="dialog-ok" (click)="onCloseDialog()">OK</button>
      </div>
  </div>
`,
    styleUrls: ['./healthdashbord.component.css'],
    standalone: false
})
export class ConfirmationDialogComponent {
  profData: any;
  selectedOption: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,private toast: ToastrService,
    public dialogRef: MatDialogRef<ConfirmationDialogComponent>
  ) { }

  ngOnInit() {
      //console.log('selectedOption',this.selectedOption)
      //localStorage.removeItem('validationDone');
  }

  onCloseDialog() {
    // Only close the dialog if an option is selected
    // if (this.selectedOption) {
    //   this.closeDialog();
    // } 
    // else{
    //   this.toast.error('Please choose a Gmail or Outlook account.')
    // }
    this.closeDialog();
  }
  closeDialog() {
    this.dialogRef.close()
  }
}